<template>
  <div class="flex flex-col gap-4">
    <div v-if="showHeader" class="flex items-center gap-3 mb-6">
      <div class="flex items-center justify-center w-10 h-10 rounded-full bg-amber-50 dark:bg-amber-900/30">
        <UIcon name="i-heroicons-shield-exclamation" class="w-5 h-5 text-amber-600 dark:text-amber-400" />
      </div>
      <div>
        <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Report Suspicious Point Code</h3>
        <p class="text-sm text-gray-500 dark:text-gray-400">Log failed or suspicious point code activity for this customer</p>
      </div>
    </div>

    <form class="space-y-4" @submit.prevent="handleSubmit">
      <UAlert
        v-if="optionsLoadError"
        color="error"
        variant="soft"
        :title="optionsLoadError"
        icon="i-heroicons-exclamation-triangle-20-solid"
      />

      <div v-if="!embedded && wuId <= 0" class="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <UFormField label="Customer name" required>
          <UInput
            :model-value="customerName"
            placeholder="Customer name"
            size="md"
            class="w-full"
            @update:model-value="emit('update:customerName', $event)"
          />
        </UFormField>

        <UFormField label="Customer email" required>
          <UInput
            :model-value="customerEmail"
            type="email"
            placeholder="customer@example.com"
            size="md"
            class="w-full"
            @update:model-value="emit('update:customerEmail', $event)"
          />
        </UFormField>
      </div>

      <div class="grid grid-cols-1 gap-3">
        <UFormField label="Point code">
          <UInput
            v-model="form.failedPointCode"
            placeholder="e.g. L10-22P-4CC"
            size="md"
            class="w-full"
          />
        </UFormField>

        <UFormField label="Reason" required>
          <USelectMenu
            v-model="form.failedPointCodeReason"
            :items="reasonItems"
            value-key="value"
            label-key="label"
            placeholder="Why is this suspicious?"
            size="md"
            class="w-full"
            :loading="optionsLoading"
          />
        </UFormField>

        <UFormField label="Status">
          <USelectMenu
            v-model="form.resolutionStatus"
            :items="resolutionItems"
            value-key="value"
            label-key="label"
            placeholder="Select status"
            size="md"
            class="w-full"
            :loading="optionsLoading"
          />
        </UFormField>

        <UFormField label="Notes">
          <UTextarea
            v-model="form.adminNotes"
            placeholder="Anything useful for follow-up…"
            :rows="2"
            size="md"
            class="w-full"
          />
        </UFormField>
      </div>

      <UCollapsible v-model:open="optionalOpen" class="rounded-lg border border-default">
        <button
          type="button"
          class="flex w-full items-center justify-between gap-2 px-3 py-2.5 text-left text-sm text-text-secondary hover:text-text-primary transition-colors"
        >
          <span class="font-medium">Purchase details</span>
          <span class="flex items-center gap-1.5 text-xs">
            Optional
            <UIcon
              name="i-heroicons-chevron-down-20-solid"
              class="h-4 w-4 transition-transform"
              :class="{ 'rotate-180': optionalOpen }"
            />
          </span>
        </button>
        <template #content>
          <div class="grid grid-cols-1 gap-3 border-t border-default px-3 py-3">
            <UFormField label="Product">
              <USelectMenu
                v-model="form.productId"
                :items="productItems"
                value-key="value"
                label-key="label"
                placeholder="Select product"
                size="md"
                class="w-full"
                :loading="productsLoading"
              />
            </UFormField>
            <UFormField label="Store">
              <UInput
                v-model="form.storePurchased"
                placeholder="e.g. Matas Vejle"
                size="md"
                class="w-full"
              />
            </UFormField>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <UFormField label="Date purchased">
                <UInput
                  v-model="form.datePurchased"
                  type="date"
                  size="md"
                  class="w-full"
                />
              </UFormField>
              <UFormField label="Batch number">
                <UInput
                  v-model="form.batchNumber"
                  placeholder="Optional"
                  size="md"
                  class="w-full"
                />
              </UFormField>
            </div>
          </div>
        </template>
      </UCollapsible>

      <div v-if="submitError" class="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-3">
        <div class="flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
          <UIcon name="i-heroicons-exclamation-triangle-20-solid" class="w-5 h-5 shrink-0" />
          <span>{{ submitError }}</span>
        </div>
      </div>

      <div class="flex justify-end gap-2 pt-1">
        <UButton
          v-if="!embedded"
          color="neutral"
          variant="outline"
          label="Cancel"
          :disabled="submitting"
          @click="emit('close')"
        />
        <UButton
          v-else
          color="neutral"
          variant="ghost"
          label="Reset"
          :disabled="submitting"
          @click="clearForm"
        />
        <UButton
          type="submit"
          color="primary"
          label="Submit report"
          :loading="submitting"
          :disabled="!isFormValid"
        />
      </div>
    </form>

    <UCollapsible
      v-if="wuId > 0"
      v-model:open="historyOpen"
      class="rounded-lg border border-default"
    >
      <button
        type="button"
        class="flex w-full items-center justify-between gap-2 px-3 py-2.5 text-left text-sm transition-colors hover:text-text-primary"
      >
        <span class="flex items-center gap-2 font-medium text-text-primary">
          <UIcon name="i-heroicons-clock-20-solid" class="h-4 w-4 text-text-secondary" />
          Previous reports
          <UBadge v-if="reportCount" variant="subtle" color="neutral" size="sm">
            {{ reportCount }}
          </UBadge>
        </span>
        <UIcon
          name="i-heroicons-chevron-down-20-solid"
          class="h-4 w-4 text-text-secondary transition-transform"
          :class="{ 'rotate-180': historyOpen }"
        />
      </button>

      <template #content>
        <div class="space-y-4 border-t border-default px-3 py-3">
          <UAlert
            v-if="logsLoadError"
            color="error"
            variant="soft"
            :title="logsLoadError"
            icon="i-heroicons-exclamation-triangle-20-solid"
          />

          <div v-if="logsLoading" class="flex flex-col items-center justify-center py-8 gap-3">
            <UIcon name="i-heroicons-arrow-path-20-solid" class="w-8 h-8 animate-spin text-primary" />
            <span class="text-sm text-gray-500">Loading reports...</span>
          </div>

          <template v-else-if="customerReports.length">
            <div class="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 p-3 space-y-3">
              <div class="flex flex-wrap items-center gap-2">
                <UInput
                  v-model="historySearchQuery"
                  icon="i-heroicons-magnifying-glass-20-solid"
                  size="sm"
                  color="neutral"
                  placeholder="Search reports..."
                  class="w-full sm:flex-1 sm:min-w-40"
                />

                <USelectMenu
                  :model-value="historyReasonFilter ?? undefined"
                  :items="historyReasonFilterItems"
                  value-key="value"
                  label-key="label"
                  placeholder="Reason"
                  size="sm"
                  color="neutral"
                  class="w-full sm:w-36"
                  @update:model-value="historyReasonFilter = $event ?? null"
                />

                <USelectMenu
                  :model-value="historyResolutionFilter ?? undefined"
                  :items="historyResolutionFilterItems"
                  value-key="value"
                  label-key="label"
                  placeholder="Status"
                  size="sm"
                  color="neutral"
                  class="w-full sm:w-36"
                  @update:model-value="historyResolutionFilter = $event ?? null"
                />

                <UButton
                  v-if="hasActiveHistoryFilters"
                  icon="i-heroicons-x-mark-20-solid"
                  label="Clear"
                  color="neutral"
                  variant="ghost"
                  size="sm"
                  @click="clearHistoryFilters"
                />
              </div>

              <p v-if="hasActiveHistoryFilters" class="text-xs text-gray-500">
                Showing {{ filteredCustomerReports.length }} of {{ customerReports.length }} reports
              </p>
            </div>

            <div v-if="!filteredCustomerReports.length" class="flex flex-col items-center justify-center py-8 gap-3">
              <UIcon name="i-heroicons-funnel" class="w-10 h-10 text-gray-300" />
              <span class="text-sm text-gray-500">No reports match your search or filters</span>
            </div>

            <UTimeline
              v-else
              :items="historyTimelineItems"
              color="primary"
              size="sm"
              class="w-full max-h-[40vh] overflow-y-auto pr-1"
              :ui="{ date: 'text-xs text-gray-500 tabular-nums whitespace-nowrap', title: 'font-mono text-sm font-semibold text-gray-900 dark:text-white' }"
            >
              <template #report-date="{ item }">
                <div class="text-right">
                  <span class="block text-[10px] font-medium uppercase tracking-wide text-gray-400">Logged</span>
                  <span class="text-xs text-gray-500 tabular-nums">{{ item.date }}</span>
                </div>
              </template>

              <template #report-description="{ item }">
                <div class="mt-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/50 overflow-hidden">
                  <div class="flex flex-wrap items-center gap-2 px-3 py-2 border-b border-gray-100 dark:border-gray-800 bg-gray-50/80 dark:bg-gray-900/80">
                    <UBadge variant="subtle" color="warning" size="sm">
                      {{ getReasonLabel(item.log.failedPointCodeReason) }}
                    </UBadge>
                    <UBadge variant="subtle" :color="getResolutionBadgeColor(item.log.resolutionStatus)" size="sm">
                      {{ getResolutionLabel(item.log.resolutionStatus) }}
                    </UBadge>
                  </div>

                  <div class="px-3 py-3 space-y-2.5 text-sm">
                    <div
                      v-for="detail in getReportDetails(item.log)"
                      :key="detail.label"
                      class="flex items-start gap-3 min-w-0"
                    >
                      <span class="w-24 shrink-0 text-xs font-medium text-gray-500 pt-0.5">{{ detail.label }}</span>
                      <span class="text-gray-900 dark:text-gray-100 break-words">{{ detail.value }}</span>
                    </div>
                  </div>

                  <div
                    v-if="item.log.adminNotes"
                    class="px-3 py-2.5 border-t border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-900/30 text-sm text-gray-600 dark:text-gray-400"
                  >
                    <span class="text-xs font-medium uppercase tracking-wide text-gray-500 block mb-1">Note</span>
                    {{ item.log.adminNotes }}
                  </div>
                </div>
              </template>
            </UTimeline>
          </template>

          <div v-else class="flex flex-col items-center justify-center py-8 gap-3">
            <UIcon name="i-heroicons-document-text" class="w-10 h-10 text-gray-300" />
            <span class="text-sm text-gray-500">No previous reports for this customer</span>
          </div>
        </div>
      </template>
    </UCollapsible>
  </div>
</template>


<script lang="ts" setup>
import type { PointshopProduct } from '~/types/api/pointshop/products'
import type {
  FailedPointCodeLog,
  FailedPointCodeLogOptionsResponse,
} from '~/types/api/client/pointshop'
import type { GetPointshopProductsResponse } from '~/types/api/pointshop/products'

interface Props {
  clientId: number
  wuId?: number
  customerName?: string
  customerEmail?: string
  initialReport?: {
    failedPointCode?: string
    failedPointCodeReason?: string
    adminNotes?: string
  }
  active?: boolean
  embedded?: boolean
  showHeader?: boolean
}

const {
  clientId,
  wuId = 0,
  customerName = '',
  customerEmail = '',
  initialReport,
  active = true,
  embedded = false,
  showHeader = true,
} = defineProps<Props>()

const emit = defineEmits<{
  success: []
  close: []
  'update:customerName': [value: string]
  'update:customerEmail': [value: string]
}>()

const toast = useToast()

const {
  customerReports,
  reportCount,
  loading: logsLoading,
  refresh: fetchFailedPointCodes,
} = useFailedPointCodeReports(
  () => clientId,
  () => (wuId > 0 ? wuId : -1)
)

const optionalOpen = ref(false)
const historyOpen = ref(false)

const optionsLoading = ref(false)
const productsLoading = ref(false)
const optionsLoadError = ref('')
const resolutionStatusOptions = ref<Record<string, string>>({})
const failedPointCodeReasonOptions = ref<Record<string, string>>({})
const clientProducts = ref<PointshopProduct[]>([])

const mapOptionsToItems = (options: Record<string, string>) =>
  Object.entries(options).map(([value, label]) => ({ value, label }))

const resolutionItems = computed(() => mapOptionsToItems(resolutionStatusOptions.value))
const reasonItems = computed(() => mapOptionsToItems(failedPointCodeReasonOptions.value))

const productItems = computed(() =>
  clientProducts.value.map(p => ({
    value: p.prodId,
    label: `${p.prodName} (${p.websiteName})`,
    websiteId: p.websiteId,
  }))
)

const selectedProduct = computed(() =>
  clientProducts.value.find(p => p.prodId === form.value.productId) ?? null
)

const fetchOptions = async () => {
  optionsLoading.value = true
  optionsLoadError.value = ''
  try {
    const response = await $fetch<FailedPointCodeLogOptionsResponse>(
      '/api/client/webusers/pointshop/failed-point-code-options',
      { headers: useRequestHeaders(['cookie']) as HeadersInit }
    )
    resolutionStatusOptions.value = response.resolutionStatusOptions ?? {}
    failedPointCodeReasonOptions.value = response.failedPointCodeReasonOptions ?? {}
  } catch (error: unknown) {
    const err = error as { statusMessage?: string; message?: string }
    optionsLoadError.value = err.statusMessage || err.message || 'Failed to load form options'
  } finally {
    optionsLoading.value = false
  }
}

const fetchProducts = async () => {
  productsLoading.value = true
  try {
    const response = await $fetch<GetPointshopProductsResponse>(
      '/api/client/webusers/pointshop/products',
      {
        query: { clientId },
        headers: useRequestHeaders(['cookie']) as HeadersInit,
      }
    )
    const client = response.clients?.find(c => c.clientId === clientId)
    clientProducts.value = client?.pointshopProducts ?? response.clients?.[0]?.pointshopProducts ?? []
  } catch (error) {
    console.error('Failed to fetch pointshop products:', error)
    clientProducts.value = []
  } finally {
    productsLoading.value = false
  }
}

const submitting = ref(false)
const submitError = ref('')

const form = ref({
  failedPointCode: '',
  failedPointCodeReason: '' as string,
  resolutionStatus: 'PEND' as string,
  productId: null as number | null,
  storePurchased: '',
  datePurchased: '',
  batchNumber: '',
  adminNotes: '',
})

const isFormValid = computed(() => {
  if (!form.value.failedPointCodeReason) return false
  // Embedded parent owns customer identity; modal without wuId still needs name/email here.
  if (!embedded && wuId <= 0) {
    return customerName.trim() !== '' && customerEmail.trim() !== ''
  }
  if (embedded) {
    return customerName.trim() !== '' && customerEmail.trim() !== ''
  }
  return true
})

const formatDateForApi = (dateValue: string): string => {
  if (!dateValue) return ''
  const date = new Date(dateValue)
  if (Number.isNaN(date.getTime())) return ''
  const day = date.getDate().toString().padStart(2, '0')
  const month = date.toLocaleString('en-US', { month: 'short' })
  const year = date.getFullYear().toString().slice(-2)
  return `${day}-${month}-${year}`
}

const clearForm = () => {
  form.value = {
    failedPointCode: '',
    failedPointCodeReason: '',
    resolutionStatus: 'PEND',
    productId: null,
    storePurchased: '',
    datePurchased: '',
    batchNumber: '',
    adminNotes: '',
  }
  submitError.value = ''
}

const applyInitialReport = () => {
  if (!initialReport) return

  form.value = {
    ...form.value,
    failedPointCode: initialReport.failedPointCode ?? form.value.failedPointCode,
    failedPointCodeReason: initialReport.failedPointCodeReason ?? form.value.failedPointCodeReason,
    adminNotes: initialReport.adminNotes ?? form.value.adminNotes,
  }
}

const resetPanelState = () => {
  clearForm()
  clearHistoryFilters()
  optionalOpen.value = false
  historyOpen.value = false
  logsLoadError.value = ''
}

const handleSubmit = async () => {
  if (!isFormValid.value) return

  submitting.value = true
  submitError.value = ''

  const websiteId = selectedProduct.value?.websiteId ?? clientProducts.value[0]?.websiteId

  try {
    await $fetch('/api/client/webusers/pointshop/log-failed-point-code', {
      method: 'POST',
      headers: useRequestHeaders(['cookie']) as HeadersInit,
      body: {
        clientId,
        customerName,
        customerEmail,
        usedByWebUserId: wuId > 0 ? wuId : undefined,
        failedPointCode: form.value.failedPointCode.trim() || undefined,
        failedPointCodeReason: form.value.failedPointCodeReason,
        resolutionStatus: form.value.resolutionStatus || undefined,
        productId: form.value.productId ?? undefined,
        website_id: websiteId,
        storePurchased: form.value.storePurchased.trim() || undefined,
        datePurchased: formatDateForApi(form.value.datePurchased) || undefined,
        batchNumber: form.value.batchNumber.trim() || undefined,
        adminNotes: form.value.adminNotes.trim() || undefined,
      },
    })

    toast.add({
      title: 'Report Submitted',
      description: 'The failed point code has been logged successfully.',
      color: 'success',
      icon: 'i-heroicons-check-circle-20-solid',
    })

    emit('success')
    clearForm()
    await loadFailedPointCodeHistory()
  } catch (error: unknown) {
    const err = error as { data?: { statusMessage?: string }; statusMessage?: string; message?: string }
    const message = err?.data?.statusMessage || err?.statusMessage || err?.message || 'An unexpected error occurred'
    submitError.value = message
    toast.add({
      title: 'Error',
      description: message,
      color: 'error',
      icon: 'i-heroicons-exclamation-triangle-20-solid',
    })
  } finally {
    submitting.value = false
  }
}

const logsLoadError = ref('')
const historySearchQuery = ref('')
const historyReasonFilter = ref<string | null>(null)
const historyResolutionFilter = ref<string | null>(null)

const historyReasonFilterItems = computed(() => reasonItems.value)
const historyResolutionFilterItems = computed(() => resolutionItems.value)

const hasActiveHistoryFilters = computed(() =>
  historySearchQuery.value.trim() !== '' ||
  historyReasonFilter.value !== null ||
  historyResolutionFilter.value !== null
)

const clearHistoryFilters = () => {
  historySearchQuery.value = ''
  historyReasonFilter.value = null
  historyResolutionFilter.value = null
}

const getReasonLabel = (code: string) =>
  failedPointCodeReasonOptions.value[code] ?? code

const getResolutionLabel = (code: string) =>
  resolutionStatusOptions.value[code] ?? code

const filteredCustomerReports = computed(() => {
  let filtered = customerReports.value

  if (historyReasonFilter.value) {
    filtered = filtered.filter(log => log.failedPointCodeReason === historyReasonFilter.value)
  }

  if (historyResolutionFilter.value) {
    filtered = filtered.filter(log => log.resolutionStatus === historyResolutionFilter.value)
  }

  const query = historySearchQuery.value.trim().toLowerCase()
  if (query) {
    filtered = filtered.filter((log) => {
      const searchable = [
        log.failedPointCode,
        log.storePurchased,
        log.batchNumber,
        log.adminNotes,
        log.datePurchased,
        log.dateLogged,
        log.productName,
        log.productId != null ? String(log.productId) : '',
        getReasonLabel(log.failedPointCodeReason),
        getResolutionLabel(log.resolutionStatus),
      ]

      return searchable.some(value => value?.toLowerCase().includes(query))
    })
  }

  return filtered
})

interface ReportTimelineItem {
  date: string
  title: string
  description: string
  icon: string
  slot: 'report'
  log: FailedPointCodeLog
}

const getResolutionIcon = (code: string) => {
  const map: Record<string, string> = {
    PEND: 'i-heroicons-clock',
    REJE: 'i-heroicons-x-circle',
    POCR: 'i-heroicons-check-circle',
  }
  return map[code] ?? 'i-heroicons-shield-exclamation'
}

const getResolutionBadgeColor = (code: string): 'warning' | 'error' | 'success' | 'neutral' => {
  const map: Record<string, 'warning' | 'error' | 'success' | 'neutral'> = {
    PEND: 'warning',
    REJE: 'error',
    POCR: 'success',
  }
  return map[code] ?? 'neutral'
}

const formatProductLabel = (log: FailedPointCodeLog) => {
  if (log.productName && log.productId) {
    return `${log.productName} (ID ${log.productId})`
  }
  if (log.productName) return log.productName
  if (log.productId) return `ID ${log.productId}`
  return null
}

const getReportDetails = (log: FailedPointCodeLog) => {
  const details: { label: string; value: string }[] = []

  if (log.datePurchased) {
    details.push({ label: 'Purchased', value: formatFailedPointCodeDate(log.datePurchased) })
  }
  if (log.storePurchased) {
    details.push({ label: 'Store', value: log.storePurchased })
  }
  if (log.batchNumber) {
    details.push({ label: 'Batch', value: log.batchNumber })
  }

  const product = formatProductLabel(log)
  if (product) {
    details.push({ label: 'Product', value: product })
  }

  return details
}

const historyTimelineItems = computed<ReportTimelineItem[]>(() =>
  filteredCustomerReports.value.map((log, index) => ({
    date: formatFailedPointCodeDate(log.dateLogged),
    title: log.failedPointCode,
    description: ' ',
    icon: getResolutionIcon(log.resolutionStatus),
    slot: 'report',
    log,
    value: `${log.failedPointCode}-${index}`,
  }))
)

const loadFailedPointCodeHistory = async () => {
  logsLoadError.value = ''
  try {
    await fetchFailedPointCodes()
  } catch (error: unknown) {
    const err = error as { statusMessage?: string; message?: string }
    logsLoadError.value = err.statusMessage || err.message || 'Failed to load failed point code reports'
  }
}

const loadPanelData = async () => {
  await Promise.all([fetchOptions(), fetchProducts()])
  if (wuId > 0) {
    await loadFailedPointCodeHistory()
  }
}

watch(() => active, (active) => {
  if (active) {
    applyInitialReport()
    loadPanelData()
  } else {
    resetPanelState()
  }
}, { immediate: true })

watch(() => initialReport, () => {
  if (active) applyInitialReport()
}, { deep: true })

watch(historyOpen, (open) => {
  if (open && active && wuId > 0 && !logsLoading.value && reportCount.value === 0) {
    loadFailedPointCodeHistory()
  }
})

watch(() => clientId, () => {
  if (active && embedded) {
    resetPanelState()
    loadPanelData()
  }
})
</script>
